import { LANGUAGE_CODE_MAP, LANG_ATTRIBUTE_TAG } from "@/constants/Language";
import ArticleLevel from "@/types/models/ArticleLevel.interface";
import { Assignment } from "@/types/models/Assignment.interface";
import {
  IRecommendedReadingLevel,
  ILegacyStudentReadingLevel,
} from "@/types/models/ReadingLevel.interface";
import { User } from "@/types/models/User.interface";
import getUserProperties from "@/users/getUserProperties";
import getCurrentLevelByNumericGrade from "@/utils/getCurrentLevelByNumericGrade";
import findDefaultLevelFromReadingOrGradeLevel from "@/view/utils/findDefaultLevelFromReadingOrGradeLevel";
import findLevelLockedContent from "@/view/utils/findLevelLockedContent";

const getCurrentLevel = ({
  articleLevels = [],
  contentId,
  levelId = "",
  matchedAssignment = {},
  user,
  studentReadingLevel,
}: {
  articleLevels?: ArticleLevel[] | undefined;
  contentId: string;
  levelId?: string | undefined;
  matchedAssignment: Assignment | Record<string, never>;
  user: User;
  studentReadingLevel:
    | IRecommendedReadingLevel
    | ILegacyStudentReadingLevel
    | undefined;
}) => {
  const { isStudent } = getUserProperties(user);
  const { assignment_members: assignmentMembers = [] } = matchedAssignment;
  const sortedLevels = [...articleLevels].sort(
    (a, b) => a.raw_lexile - b.raw_lexile,
  );
  const levelIdLanguage = sortedLevels.find(
    (level) => level.id === levelId,
  )?.language;

  const assignmentLanguage =
    assignmentMembers?.[0]?.language ||
    // @ts-expect-error when handling nested assignment members
    assignmentMembers?.[0]?.content?.language;

  const studentReadingLevelLanguage = (
    studentReadingLevel as IRecommendedReadingLevel
  )?.language;

  const language = (assignmentLanguage ||
    levelIdLanguage ||
    studentReadingLevelLanguage) as LANG_ATTRIBUTE_TAG;

  const currentLangCode = language ? LANGUAGE_CODE_MAP[language] : "LANG_EN";

  // Uses new field.
  if (isStudent && matchedAssignment?.default_reading_level) {
    // This logic will need to be updated once the MVE flow supports multi-asset assignments,
    // to allow for the possibility that there is not a level that directly matches the default reading level.
    const level = getCurrentLevelByNumericGrade(
      sortedLevels,
      matchedAssignment?.default_reading_level,
      currentLangCode,
    );
    if (level) {
      return level;
    }
  }

  // Uses legacy field.
  if (isStudent && matchedAssignment?.grade_level_lock) {
    return findLevelLockedContent(matchedAssignment, sortedLevels, contentId);
  }

  // Uses new fields.
  if (
    isStudent &&
    !matchedAssignment?.default_reading_level &&
    matchedAssignment?.is_reading_level_locked
  ) {
    return findDefaultLevelFromReadingOrGradeLevel(
      user,
      sortedLevels,
      currentLangCode,
      studentReadingLevel,
    );
  }

  if (levelId) {
    const level = sortedLevels.find((level) => level.id === levelId);
    const levelLanguageMatch =
      !currentLangCode || level?.language === currentLangCode;

    if (level && levelLanguageMatch) {
      return level;
    }
  }

  return findDefaultLevelFromReadingOrGradeLevel(
    user,
    sortedLevels,
    currentLangCode,
    studentReadingLevel,
  );
};

export default getCurrentLevel;
